import { Alert, Box, CircularProgress, Grid } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useParams } from 'react-router-dom';
import { UserPermissionsForm } from '../components/Admin/UserPermissionsForm';
import ConfirmationDialog from '../components/Dialogs/ConfirmationDialog';
import { UserDetailsForm } from './UserDetails/UserDetailsForm';
import { UserDetailsHeader } from './UserDetails/UserDetailsHeader';
import { UserResourceAllocation } from './UserDetails/UserResourceAllocation';
import { UserSecurityActions } from './UserDetails/UserSecurityActions';
import { useUserDetails } from './UserDetails/useUserDetails';

const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const state = useUserDetails(userId);

  if (state.loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!state.user) {
    return null;
  }

  return (
    <PageContainer>
      <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
        <UserDetailsHeader
          user={state.user}
          canEditUser={state.canEditUser}
          isSaving={state.isSaving}
          onSave={state.handleSave}
          onBack={() => state.navigate(-1)}
        />

        {state.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {state.error}
          </Alert>
        )}

        {state.user.status === 'pending' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This user has an outstanding invitation and has not yet completed onboarding. The
            settings configured below will be applied when they register.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <UserDetailsForm
                user={state.user}
                formData={state.formData}
                teams={state.teams}
                canEditUser={state.canEditUser}
                canManageRoles={state.canManageRoles}
                canManageSystem={state.canManageSystem}
                onChange={state.handleChange}
                onSelectChange={state.handleSelectChange}
              />
              <UserResourceAllocation
                user={state.user}
                formData={state.formData}
                canEditUser={state.canEditUser}
                getAvailability={state.getAvailability}
                onLimitChange={state.handleLimitChange}
                onManageTeamAllocations={() =>
                  state.navigate(`/admin/team/${state.formData.teamId}`)
                }
              />
              <UserSecurityActions
                user={state.user}
                canEditUser={state.canEditUser}
                onResetPassword={state.handleResetPassword}
                onResendInvite={state.handleResendInvitation}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <UserPermissionsForm
              role={state.formData.role}
              permissions={state.formData.permissions}
              isCustomPermissions={state.formData.isCustomPermissions}
              canEditUser={state.canEditUser}
              canManageRoles={state.canManageRoles}
              canManageSystem={state.canManageSystem}
              onCustomSwitchChange={state.handleCustomSwitch}
              onPermissionToggle={state.handlePermissionToggle}
            />
          </Grid>
        </Grid>
      </Box>

      <ConfirmationDialog
        open={state.confirmationDialogOpen}
        onClose={() => state.setConfirmationDialogOpen(false)}
        isLoading={state.isConfirming}
        onConfirm={
          state.confirmationConfig.onConfirm ||
          (() => {
            // No-op
          })
        }
        title={state.confirmationConfig.title}
        contentText={state.confirmationConfig.description}
      />
    </PageContainer>
  );
};

export default UserDetailsPage;
